import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, useMediaQuery } from '@mui/material';
import { getChatMessages } from 'api/chat';
import {
  fetchMakeChatUserRead,
  fetchMakeChatUserUnread,
  fetchUpdateChatUserDontDisturb,
  fetchUpdateChatUserIcon,
} from 'api/chat-users';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'store';
import { updateSingleChatUser } from 'store/slices/chat';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { DeleteUsersConfirmation } from 'types/chat-users/chat-users-list';
import { clickPreventDefaultAndStopPropagation } from 'utils/helpers';

interface ChatUserActionsProps {
  user: ChatUserModel;
  handleDeleteUsersConfirmation: (params: Partial<DeleteUsersConfirmation>) => void;
}

const UserActions: React.FC<ChatUserActionsProps> = ({ user, handleDeleteUsersConfirmation }) => {
  const showAvatar = useSelector((state) => state.userConfig.isAvatarActive);
  const intl = useIntl();

  const matchesBigMobile = useMediaQuery((theme) => theme.breakpoints.down(768));
  const [additionalMenuAnchorEl, setAdditionalMenuAnchorEl] = React.useState<Element | null>(null);

  const handleAdditionalMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    clickPreventDefaultAndStopPropagation(e);
    setAdditionalMenuAnchorEl(e.currentTarget);
  };
  const handleAdditionalMenuClose = () => {
    setAdditionalMenuAnchorEl(null);
  };
  const onMenuOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
    clickPreventDefaultAndStopPropagation(e);
    handleAdditionalMenuClose();
  };

  //* actions
  const makeChatUserUnread = async () => {
    try {
      let lastIncomingMessageId = null;
      const whatsappPhoneNumber = user.UserDisplayPhoneNumber;
      if (user.incomingLastMessageId) {
        lastIncomingMessageId = user.incomingLastMessageId;
      } else {
        const messagesRes = await getChatMessages(1, whatsappPhoneNumber, 50);

        if (messagesRes?.messages?.length) {
          const lastIncomingMessage = messagesRes?.messages.find((msg) => msg.Type === 'incoming');
          lastIncomingMessageId = lastIncomingMessage && lastIncomingMessage.Id;
        }
      }

      if (lastIncomingMessageId) {
        fetchMakeChatUserUnread(whatsappPhoneNumber, lastIncomingMessageId);
      }
    } catch (error) {
      console.warn('makeChatUserUnread error', error);
    }
  };
  const makeSingleChatUserRead = async () => {
    fetchMakeChatUserRead(user.Id);
  };
  const updateChatUserIcon = async () => {
    const data = await fetchUpdateChatUserIcon(user.UserDisplayPhoneNumber);
    if (data?.result) {
      updateSingleChatUser(user.Id, {
        UserPic: data.userPick,
      });
    } else {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'chat.single-chat-user.additional-menu.get-user-pic.error',
        }),
        {
          variant: 'warning',
        },
      );
    }
  };
  const updateChatUserDontDisturb = async () => {
    const data = await fetchUpdateChatUserDontDisturb(user.UserDisplayPhoneNumber, !user.DontDisturb);
    if (data?.result) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'chat.single-chat-user.additional-menu.dont-disturb.success',
        }),
        {
          variant: 'success',
        },
      );
    } else {
      enqueueSnackbar(
        data?.message ||
          intl.formatMessage({
            id: 'chat.single-chat-user.additional-menu.dont-disturb.error',
          }),
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <React.Fragment>
      <IconButton
        size="small"
        sx={
          // applicationType === 'extension' || matchesBigMobile
          matchesBigMobile
            ? {
                width: 30,
                height: 30,
              }
            : null
        }
        aria-label="more"
        // ref={moreBtnRef}
        color="primary"
        onClick={handleAdditionalMenuClick}
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={additionalMenuAnchorEl}
        open={Boolean(additionalMenuAnchorEl)}
        onClose={handleAdditionalMenuClose}
        disableScrollLock={true}
      >
        <MenuItem
          onClick={(e) => {
            onMenuOptionClick(e);
            makeChatUserUnread();
          }}
        >
          <FormattedMessage id="chat.single-chat-user.additional-menu.make-unread" />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            onMenuOptionClick(e);
            makeSingleChatUserRead();
          }}
        >
          <FormattedMessage id="chat.single-chat-user.additional-menu.make-read" />
        </MenuItem>
        {showAvatar && (
          <MenuItem
            onClick={(e) => {
              onMenuOptionClick(e);
              updateChatUserIcon();
            }}
          >
            <FormattedMessage id="chat.single-chat-user.additional-menu.get-user-pic" />
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            onMenuOptionClick(e);
            updateChatUserDontDisturb();
          }}
        >
          {user?.DontDisturb ? (
            <FormattedMessage id="chat.single-chat-user.additional-menu.remove-dont-disturb" />
          ) : (
            <FormattedMessage id="chat.single-chat-user.additional-menu.dont-disturb" />
          )}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            onMenuOptionClick(e);
            handleDeleteUsersConfirmation({
              show: true,
              users: [user.UserDisplayPhoneNumber],
            });
          }}
        >
          <FormattedMessage id="chat.single-chat-user.additional-menu.delete" />
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};

export default UserActions;
